/**
 * Shields Exterior Services — live indicative estimate engine.
 *
 * Estimates are guidance only, not a confirmed quote. The final customer price
 * must be reviewed by Shields and confirmed after a suitable partner has been
 * selected and the job details/photos have been assessed.
 *
 * Rates are intentionally centralised so the business can tune them against
 * actual partner pricing and local operating costs before launch.
 */
import type { DimensionKey } from "./exterior-services";

export type EstimateInput = {
  services: string[];
  dimensions?: Partial<Record<DimensionKey, number>>;
  propertyType?: "house" | "flat" | "commercial" | "other";
  postcode?: string;
  photoCount?: number;
};

export type ServiceEstimate = {
  service: string;
  label: string;
  quantity: number | null;
  unit: "m²" | "linear m" | "window" | "panel" | "job";
  low: number;
  high: number;
  note?: string;
};

export type LiveEstimate = {
  currency: "GBP";
  low: number;
  high: number;
  services: ServiceEstimate[];
  confidence: "indicative" | "needs-review";
  message: string;
  requiresManualReview: boolean;
};

type Rate = {
  slug: string;
  label: string;
  unit: ServiceEstimate["unit"];
  dimensionKeys: DimensionKey[];
  lowRate: number;
  highRate: number;
  minimum: number;
  defaultLow: number;
  defaultHigh: number;
  note?: string;
};

// Starter assumptions only. Validate with real Shields partner prices before launch.
const RATES: Rate[] = [
  { slug: "driveway-cleaning", label: "Driveway cleaning", unit: "m²", dimensionKeys: ["driveway_length_m", "driveway_width_m"], lowRate: 3.5, highRate: 5.5, minimum: 120, defaultLow: 120, defaultHigh: 220, note: "Standard wash only. Heavy moss, oil treatment, re-sanding and sealing may cost extra." },
  { slug: "patio-cleaning", label: "Patio cleaning", unit: "m²", dimensionKeys: ["patio_length_m", "patio_width_m"], lowRate: 4, highRate: 6.5, minimum: 100, defaultLow: 100, defaultHigh: 200, note: "Standard wash only. Very dirty surfaces or specialist treatment may cost extra." },
  { slug: "pressure-washing", label: "Pressure washing", unit: "m²", dimensionKeys: ["surface_area_m2"], lowRate: 2.5, highRate: 5, minimum: 80, defaultLow: 80, defaultHigh: 220 },
  { slug: "roof-cleaning", label: "Roof cleaning", unit: "m²", dimensionKeys: ["roof_area_m2"], lowRate: 8, highRate: 15, minimum: 350, defaultLow: 350, defaultHigh: 900, note: "Roof access, pitch and method can materially change the price." },
  { slug: "moss-removal", label: "Moss removal", unit: "m²", dimensionKeys: ["surface_area_m2"], lowRate: 3, highRate: 8, minimum: 90, defaultLow: 90, defaultHigh: 300 },
  { slug: "gutter-cleaning", label: "Gutter cleaning", unit: "linear m", dimensionKeys: ["gutter_length_m"], lowRate: 3, highRate: 6, minimum: 60, defaultLow: 60, defaultHigh: 150 },
  { slug: "gutter-repairs", label: "Gutter repairs", unit: "job", dimensionKeys: ["gutter_length_m"], lowRate: 0, highRate: 0, minimum: 90, defaultLow: 90, defaultHigh: 250, note: "Repairs require manual assessment; parts and access affect the final price." },
  { slug: "window-cleaning", label: "Window cleaning", unit: "window", dimensionKeys: ["window_count"], lowRate: 2, highRate: 5, minimum: 25, defaultLow: 25, defaultHigh: 60 },
  { slug: "fascia-soffit-cleaning", label: "Fascia & soffit cleaning", unit: "linear m", dimensionKeys: ["gutter_length_m"], lowRate: 3, highRate: 6, minimum: 70, defaultLow: 70, defaultHigh: 180 },
  { slug: "render-cleaning", label: "Render / wall cleaning", unit: "m²", dimensionKeys: ["wall_area_m2"], lowRate: 3, highRate: 7, minimum: 120, defaultLow: 120, defaultHigh: 400 },
  { slug: "soft-washing", label: "Soft washing", unit: "m²", dimensionKeys: ["wall_area_m2"], lowRate: 3, highRate: 7, minimum: 120, defaultLow: 120, defaultHigh: 400 },
  { slug: "conservatory-cleaning", label: "Conservatory cleaning", unit: "job", dimensionKeys: [], lowRate: 0, highRate: 0, minimum: 70, defaultLow: 70, defaultHigh: 180 },
  { slug: "solar-panel-cleaning", label: "Solar panel cleaning", unit: "panel", dimensionKeys: ["panel_count"], lowRate: 4, highRate: 8, minimum: 60, defaultLow: 60, defaultHigh: 150 },
  { slug: "fence-decking-cleaning", label: "Fence & decking cleaning", unit: "m²", dimensionKeys: ["surface_area_m2"], lowRate: 2.5, highRate: 5.5, minimum: 80, defaultLow: 80, defaultHigh: 250 },
  { slug: "graffiti-removal", label: "Graffiti removal", unit: "m²", dimensionKeys: ["surface_area_m2"], lowRate: 8, highRate: 20, minimum: 120, defaultLow: 120, defaultHigh: 350, note: "Surface type and paint penetration require manual review." },
  { slug: "commercial-exterior-cleaning", label: "Commercial exterior cleaning", unit: "m²", dimensionKeys: ["surface_area_m2"], lowRate: 1.5, highRate: 4, minimum: 180, defaultLow: 180, defaultHigh: 650, note: "Commercial sites require scope and access review." },
  { slug: "car-park-cleaning", label: "Car park / hard-surface cleaning", unit: "m²", dimensionKeys: ["surface_area_m2"], lowRate: 1, highRate: 3, minimum: 250, defaultLow: 250, defaultHigh: 1200, note: "Large-area access, drainage and oil treatment affect price." },
  { slug: "other-exterior-cleaning", label: "Other exterior cleaning", unit: "job", dimensionKeys: [], lowRate: 0, highRate: 0, minimum: 80, defaultLow: 80, defaultHigh: 300, note: "This request needs manual review." },
  { slug: "regular-house-cleaning", label: "Regular house cleaning", unit: "job", dimensionKeys: ["room_count", "bathroom_count"], lowRate: 8, highRate: 18, minimum: 45, defaultLow: 45, defaultHigh: 110, note: "Guide assumes a routine clean; frequency, condition and time on site change the price." },
  { slug: "deep-house-cleaning", label: "Deep house cleaning", unit: "job", dimensionKeys: ["bedroom_count", "bathroom_count"], lowRate: 35, highRate: 65, minimum: 120, defaultLow: 120, defaultHigh: 320, note: "Condition and scope can materially change the final price." },
  { slug: "end-of-tenancy-cleaning", label: "End-of-tenancy cleaning", unit: "job", dimensionKeys: ["bedroom_count", "bathroom_count"], lowRate: 40, highRate: 75, minimum: 140, defaultLow: 140, defaultHigh: 380, note: "Oven, carpets, furnished properties and heavy soiling may cost extra." },
  { slug: "move-in-cleaning", label: "Move-in cleaning", unit: "job", dimensionKeys: ["bedroom_count", "bathroom_count"], lowRate: 35, highRate: 65, minimum: 120, defaultLow: 120, defaultHigh: 320 },
  { slug: "kitchen-cleaning", label: "Kitchen cleaning", unit: "job", dimensionKeys: ["room_count"], lowRate: 35, highRate: 80, minimum: 35, defaultLow: 35, defaultHigh: 100, note: "Inside cupboards and appliances may be additional." },
  { slug: "bathroom-cleaning", label: "Bathroom cleaning", unit: "job", dimensionKeys: ["bathroom_count"], lowRate: 25, highRate: 45, minimum: 25, defaultLow: 25, defaultHigh: 60 },
  { slug: "oven-cleaning", label: "Oven cleaning", unit: "job", dimensionKeys: ["oven_count"], lowRate: 55, highRate: 100, minimum: 55, defaultLow: 55, defaultHigh: 100, note: "Oven type and condition affect the price." },
  { slug: "carpet-cleaning", label: "Carpet cleaning", unit: "m²", dimensionKeys: ["carpet_area_m2"], lowRate: 3, highRate: 5, minimum: 60, defaultLow: 60, defaultHigh: 180, note: "Indicative only; fibre type, stains and access affect price." },
  { slug: "upholstery-cleaning", label: "Upholstery-cleaning", unit: "job", dimensionKeys: ["upholstery_items"], lowRate: 30, highRate: 60, minimum: 45, defaultLow: 45, defaultHigh: 160, note: "Fabric type and item size must be checked." },
  { slug: "mattress-cleaning", label: "Mattress cleaning", unit: "job", dimensionKeys: ["upholstery_items"], lowRate: 25, highRate: 50, minimum: 35, defaultLow: 35, defaultHigh: 90 },
  { slug: "hard-floor-cleaning", label: "Hard-floor cleaning", unit: "m²", dimensionKeys: ["surface_area_m2"], lowRate: 3, highRate: 7, minimum: 60, defaultLow: 60, defaultHigh: 200, note: "Floor material and finish affect the method and price." },
  { slug: "internal-window-cleaning", label: "Internal window cleaning", unit: "window", dimensionKeys: ["window_count"], lowRate: 2, highRate: 4, minimum: 25, defaultLow: 25, defaultHigh: 70 },
  { slug: "after-builders-cleaning", label: "After-builders cleaning", unit: "job", dimensionKeys: ["bedroom_count", "bathroom_count"], lowRate: 0, highRate: 0, minimum: 180, defaultLow: 180, defaultHigh: 500, note: "Fine dust, construction residue and site condition require review." },
  { slug: "one-off-home-cleaning", label: "One-off home cleaning", unit: "job", dimensionKeys: ["room_count", "bathroom_count"], lowRate: 10, highRate: 20, minimum: 50, defaultLow: 50, defaultHigh: 160 },
  { slug: "other-interior-cleaning", label: "Other interior cleaning", unit: "job", dimensionKeys: [], lowRate: 0, highRate: 0, minimum: 50, defaultLow: 50, defaultHigh: 300, note: "This request needs manual review." },
];

const roundTo5 = (value: number) => Math.max(0, Math.round(value / 5) * 5);

function quantityFor(rate: Rate, dimensions: Partial<Record<DimensionKey, number>>): number | null {
  if (rate.slug === "driveway-cleaning" || rate.slug === "patio-cleaning") {
    const [lengthKey, widthKey] = rate.dimensionKeys;
    if (!lengthKey || !widthKey) return null;
    const a = dimensions[lengthKey];
    const b = dimensions[widthKey];
    return typeof a === "number" && typeof b === "number" && a > 0 && b > 0 ? a * b : null;
  }
  const key = rate.dimensionKeys[0];
  const q = key ? dimensions[key] : undefined;
  return typeof q === "number" && q > 0 ? q : null;
}

export function estimateExteriorCleaning(input: EstimateInput): LiveEstimate {
  const selected = [...new Set(input.services)].map((slug) => RATES.find((r) => r.slug === slug)).filter((r): r is Rate => Boolean(r));
  const dimensions = input.dimensions ?? {};
  let requiresManualReview = false;

  const estimates: ServiceEstimate[] = selected.map((rate) => {
    const quantity = quantityFor(rate, dimensions);
    const canCalculate = quantity !== null && rate.lowRate > 0 && rate.highRate > 0;
    let low = canCalculate && quantity !== null ? Math.max(rate.minimum, quantity * rate.lowRate) : rate.defaultLow;
    let high = canCalculate && quantity !== null ? Math.max(rate.minimum, quantity * rate.highRate) : rate.defaultHigh;

    // Always require human/provider review for inherently variable or safety-sensitive jobs.
    const review = ["roof-cleaning", "gutter-repairs", "graffiti-removal", "commercial-exterior-cleaning", "car-park-cleaning", "other-exterior-cleaning", "after-builders-cleaning", "other-interior-cleaning", "upholstery-cleaning"].includes(rate.slug);
    if (!canCalculate || review) requiresManualReview = true;
    if (input.propertyType === "commercial" && !review) {
      low *= 1.15;
      high *= 1.25;
    }
    return {
      service: rate.slug,
      label: rate.label,
      quantity,
      unit: rate.unit,
      low: roundTo5(low),
      high: roundTo5(Math.max(low, high)),
      ...(rate.note ? { note: rate.note } : {}),
    };
  });

  if (estimates.length === 0) {
    return {
      currency: "GBP", low: 0, high: 0, services: [], confidence: "needs-review",
      message: "Choose at least one service to see an indicative estimate.",
      requiresManualReview: true,
    };
  }

  // Multi-service requests may be completed in one visit; do not simply add every
  // upper bound as if separate call-outs are certain. Display a broad combined range.
  const low = roundTo5(estimates.reduce((sum, item) => sum + item.low, 0) * (estimates.length > 1 ? 0.9 : 1));
  const high = roundTo5(estimates.reduce((sum, item) => sum + item.high, 0) * (estimates.length > 1 ? 0.95 : 1));

  return {
    currency: "GBP",
    low,
    high: Math.max(low, high),
    services: estimates,
    confidence: requiresManualReview ? "needs-review" : "indicative",
    requiresManualReview,
    message: requiresManualReview
      ? "This is an initial guide, not a fixed quote. Shields will review your details and photos and confirm the price with an appropriate partner before any work is agreed."
      : "This is an indicative estimate based on the details entered. The final price is confirmed after Shields reviews the request and an appropriate partner accepts the job.",
  };
}

export const ESTIMATE_RATE_CARD = RATES.map(({ slug, label, unit, lowRate, highRate, minimum, defaultLow, defaultHigh }) => ({
  slug, label, unit, lowRate, highRate, minimum, defaultLow, defaultHigh,
}));
